/**
 * Estilos de borda e border-radius do widget de imagem (builder + export).
 */

export const IMAGE_BORDER_STYLES_LIST = ['solid', 'dashed', 'dotted', 'double', 'none'];

/** @param {Record<string, unknown>} content */
export function buildImageBorderWidthTuple(content) {
  const t = Math.max(0, Math.min(32, parseInt(content?.borderTopW, 10) || 0));
  const r = Math.max(0, Math.min(32, parseInt(content?.borderRightW, 10) || 0));
  const b = Math.max(0, Math.min(32, parseInt(content?.borderBottomW, 10) || 0));
  const l = Math.max(0, Math.min(32, parseInt(content?.borderLeftW, 10) || 0));
  return { t, r, b, l, hasBorder: t + r + b + l > 0 };
}

/**
 * @param {Record<string, unknown>} content
 * @returns {string} valor CSS para border-radius
 */
export function buildImageBorderRadiusCss(content) {
  const mode = content?.imageCornerMode;
  if (mode === 'advanced') {
    const h = [
      Math.max(0, Math.min(500, parseInt(content?.rTLh, 10) || 0)),
      Math.max(0, Math.min(500, parseInt(content?.rTRh, 10) || 0)),
      Math.max(0, Math.min(500, parseInt(content?.rBRh, 10) || 0)),
      Math.max(0, Math.min(500, parseInt(content?.rBLh, 10) || 0)),
    ];
    const v = [
      Math.max(0, Math.min(500, parseInt(content?.rTLv, 10) || 0)),
      Math.max(0, Math.min(500, parseInt(content?.rTRv, 10) || 0)),
      Math.max(0, Math.min(500, parseInt(content?.rBRv, 10) || 0)),
      Math.max(0, Math.min(500, parseInt(content?.rBLv, 10) || 0)),
    ];
    if (h.every((x) => x === 0) && v.every((x) => x === 0)) return '0';
    return `${h[0]}px ${h[1]}px ${h[2]}px ${h[3]}px / ${v[0]}px ${v[1]}px ${v[2]}px ${v[3]}px`;
  }
  if (mode === 'uniform') {
    const p = Math.max(0, Math.min(50, parseInt(content?.borderRadiusPct, 10) || 0));
    return `${p}%`;
  }
  return '8px';
}

/** Preset “orgânico” para border-radius elíptico (exemplo clássico). */
export function getImageOrganicRadiusPreset() {
  return {
    imageCornerMode: 'advanced',
    rTLh: 255,
    rTRh: 15,
    rBRh: 225,
    rBLh: 15,
    rTLv: 15,
    rTRv: 225,
    rBRv: 15,
    rBLv: 255,
  };
}

const _rndInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Sorteia uma forma orgânica (border-radius 8 valores) para o modo avançado.
 * Alterna cantos “grandes” e “miúdos” de forma aleatória — cada chamada é diferente.
 * @returns {Record<string, string | number>}
 */
export function getRandomOrganicImageRadiusPatch() {
  const big = () => _rndInt(130, 300);
  const sm = () => _rndInt(8, 95);
  const roll = () => (Math.random() < 0.5 ? big() : sm());

  if (Math.random() < 0.08) {
    const p = getImageOrganicRadiusPreset();
    const jitter = (n) => Math.max(8, Math.min(500, n + _rndInt(-35, 35)));
    return {
      imageCornerMode: 'advanced',
      rTLh: jitter(p.rTLh),
      rTRh: jitter(p.rTRh),
      rBRh: jitter(p.rBRh),
      rBLh: jitter(p.rBLh),
      rTLv: jitter(p.rTLv),
      rTRv: jitter(p.rTRv),
      rBRv: jitter(p.rBRv),
      rBLv: jitter(p.rBLv),
    };
  }

  return {
    imageCornerMode: 'advanced',
    rTLh: roll(),
    rTRh: roll(),
    rBRh: roll(),
    rBLh: roll(),
    rTLv: roll(),
    rTRv: roll(),
    rBRv: roll(),
    rBLv: roll(),
  };
}

/**
 * Serializa estilos da tag img para atributo style (export HTML).
 * @param {Record<string, unknown>} content
 */
export function buildImageImgInlineStyle(content) {
  const w = content?.width != null ? Math.max(10, Math.min(100, parseInt(content.width, 10) || 100)) : 100;
  const parts = [
    'max-width: 100%',
    `width: ${w}%`,
    'height: auto',
    'display: block',
    'box-sizing: border-box',
    `border-radius: ${buildImageBorderRadiusCss(content)}`,
  ];
  const { t, r, b, l, hasBorder } = buildImageBorderWidthTuple(content);
  if (hasBorder) {
    const st = String(content?.borderStyle || 'solid');
    const style = IMAGE_BORDER_STYLES_LIST.includes(st) ? st : 'solid';
    const col = String(content?.borderColor || '#e2e8f0').trim().replace(/"/g, '&quot;');
    /* Longhand: evita ambiguidade com border-radius e garante os 4 lados em todos os motores */
    parts.push(`border-top-width: ${t}px`);
    parts.push(`border-right-width: ${r}px`);
    parts.push(`border-bottom-width: ${b}px`);
    parts.push(`border-left-width: ${l}px`);
    parts.push(`border-style: ${style}`);
    parts.push(`border-color: ${col}`);
  }
  return parts.join('; ');
}

/** Objeto de estilo para React (palco). */
export function buildImageImgStyleObject(content) {
  const w = content?.width != null ? Math.max(10, Math.min(100, parseInt(content.width, 10) || 100)) : 100;
  const o = {
    maxWidth: '100%',
    width: `${w}%`,
    height: 'auto',
    display: 'block',
    boxSizing: 'border-box',
    borderRadius: buildImageBorderRadiusCss(content),
  };
  const { t, r, b, l, hasBorder } = buildImageBorderWidthTuple(content);
  if (hasBorder) {
    const st = String(content?.borderStyle || 'solid');
    o.borderTopWidth = `${t}px`;
    o.borderRightWidth = `${r}px`;
    o.borderBottomWidth = `${b}px`;
    o.borderLeftWidth = `${l}px`;
    o.borderStyle = IMAGE_BORDER_STYLES_LIST.includes(st) ? st : 'solid';
    o.borderColor = String(content?.borderColor || '#e2e8f0').trim() || '#e2e8f0';
  }
  return o;
}
